import {
  isEmail,
  isStringNullOrEmpty,
} from 'src/shared-utilities/string-helpers';
import { ValidatorRule } from 'src/app/utilities/validation/validation.interfaces';
import { Borrower } from '../../../shared/models/deal-resources/borrower.model';
import { ClientJourneyValidationMetadata } from '../../../shared/validation/client-journey-validation-metadata';
import { ModelValidator } from '../../../shared/validation/model-validator';

export enum BorrowerValidationRuleKeys {
  FirstName = 'firstName',
  LastName = 'lastName',
  Email = 'email',
  EmailFormat = 'emailFormat',
  Phone = 'phone',
  PhoneFormat = 'phoneFormat',
  Sin = 'sin',
  DateOfBirth = 'dateOfBirth',
}

export class BorrowerValidator extends ModelValidator<
  Borrower,
  ClientJourneyValidationMetadata
> {
  private firstNameValidationRule: ValidatorRule;
  get firstNameIsValid(): boolean {
    return this.firstNameValidationRule.isValid;
  }

  private lastNameValidationRule: ValidatorRule;
  get lastNameIsValid(): boolean {
    return this.lastNameValidationRule.isValid;
  }

  private emailValidationRule: ValidatorRule;
  get emailIsValid(): boolean {
    return this.emailValidationRule.isValid;
  }

  private emailFormatValidationRule: ValidatorRule;
  get emailFormatIsValid(): boolean {
    return this.emailFormatValidationRule.isValid;
  }

  private phoneValidationRule: ValidatorRule;
  get phoneIsValid(): boolean {
    return this.phoneValidationRule.isValid;
  }

  private phoneFormatValidationRule: ValidatorRule;
  get phoneFormatIsValid(): boolean {
    return this.phoneFormatValidationRule.isValid;
  }

  private sinValidationRule: ValidatorRule;
  get sinIsValid(): boolean {
    return this.sinValidationRule.isValid;
  }

  private dateOfBirthValidationRule: ValidatorRule;
  get dateOfBirthIsValid(): boolean {
    return this.dateOfBirthValidationRule.isValid;
  }

  constructor(borrower: Borrower) {
    super(borrower);

    let navigationMetadata = <ClientJourneyValidationMetadata>{
      blocksNavigation: true,
      showInMenu: true,
    };

    //first name
    this.firstNameValidationRule = this.ruleFor(
      b => b.contact.firstName,
      BorrowerValidationRuleKeys.FirstName,
      navigationMetadata,
    ).must(firstName => firstName?.length > 0);

    //last name
    this.lastNameValidationRule = this.ruleFor(
      b => b.contact.lastName,
      BorrowerValidationRuleKeys.LastName,
      navigationMetadata,
    ).must(lastName => lastName?.length > 0);

    //email - required
    this.emailValidationRule = this.ruleFor(
      b => b.contact.email,
      BorrowerValidationRuleKeys.Email,
      navigationMetadata,
    ).must(email => email?.length > 0);

    //email - format
    this.emailFormatValidationRule = this.ruleFor(
      b => b.contact.email,
      BorrowerValidationRuleKeys.EmailFormat,
      navigationMetadata,
    )
      .when(b => b.contact.email?.length > 0)
      .must(email => {
        // const emailPattern =
        //   /^(?!\.)("([^"\r\\]|\\["\r\\])*"|([-a-z0-9!#$%&'*+\/=?^_`{|}~]|(?<!\.)\.)*)(?<!\.)@[a-z0-9][\w\.-]*[a-z0-9]\.[a-z][a-z\.]*[a-z]$/;
        // return emailPattern.test(email);
        return isEmail(email);
      });

    //phone number
    this.phoneValidationRule = this.ruleFor(
      b => b.contact.cellPhone,
      BorrowerValidationRuleKeys.Phone,
      navigationMetadata,
    )
      .when(
        b =>
          !(b.contact.homePhone?.length > 0) &&
          !(b.contact.businessPhone?.length > 0),
      )
      .must(cellPhone => cellPhone?.length > 0);

    //phone number format
    this.phoneFormatValidationRule = this.ruleFor(
      b => b.contact.cellPhone,
      BorrowerValidationRuleKeys.PhoneFormat,
      navigationMetadata,
    )
      .when(
        b =>
          !(b.contact.homePhone?.length >= 10) &&
          !(b.contact.businessPhone?.length >= 10),
      )
      .must(cellPhone => cellPhone?.length >= 10);

    //SIN
    this.sinValidationRule = this.ruleFor(
      b => b.contact.sin,
      BorrowerValidationRuleKeys.Sin,
      navigationMetadata,
    )
      .when(b => b.noaConsent ?? false)
      .must(sin => !isStringNullOrEmpty(sin, true));

    this.dateOfBirthValidationRule = this.ruleFor(
      b => b.contact.dateOfBirth,
      BorrowerValidationRuleKeys.DateOfBirth,
      navigationMetadata,
    ).must(dateOfBirth => dateOfBirth != null);
  }
}
